/**
 * Instinct — the agent's LISTENER reflexes in a live voice conversation, decided by a typed-decision
 * model (TypeSafe Jev via ai.libx.js DecideClient; any `Decider` works) instead of hand-tuned rules.
 *
 * Four human instincts, each one parallel Choice call (~300-500ms):
 *  - while the user talks:            stay silent | backchannel ("mm-hm") | interject (cut in)
 *  - while the user talks OVER us:    yield | keep talking
 *  - after a barge cut a delivery:    resume | cancel | new request
 *  - when the user's turn ends:       which quick ack fits (ACK_BANK) — or none (pickAck; engine `pickAck` seam)
 *
 * Pure decision layer: it never touches audio. The host wires `hear`/`overlap`/`bargeIntent` to the
 * engine's observations and `onBackchannel`/`onInterject`/`onYield` to its actions. Too slow for the
 * audio hot path by design — the engine's fast heuristics stay authoritative; this arbitrates on top. The one
 * exception is opt-in: a host may let the engine HOLD a short overlap for the verdict (cli/voice bindOverlapInstinct).
 */
import { AIClient, DecideClient, JevAdapter, LlmDecideAdapter, type ChatOptions } from 'ai.libx.js';
import { forComponent } from '../logging';

const log = forComponent('Instinct');

/** Structural subset of ai.libx.js DecideClient.decide — keeps this module provider-agnostic. */
export interface Decider {
  decide(o: { state: unknown; questions: Record<string, unknown> }): Promise<{ answers: Record<string, { choice: string; probabilities: Record<string, number> }> }>;
}

/** Which typed-decision backend the voice judges (leak judge, instinct, second channel) run on — DecideClient options:
 *  Jev (TYPESAFE_AI_KEY), else an LLM decider on Groq gpt-oss-120b (GROQ_API_KEY; measured 16/16 on the leak lists,
 *  p50 ~640ms — 20b is NOT accurate enough), else none (strict leak shapes only). */
export function voiceDeciderOptions(env: Record<string, string | undefined> = process.env): { provider?: 'llm'; model?: string; apiKey: string } | undefined {
  if (env.TYPESAFE_AI_KEY) return { apiKey: env.TYPESAFE_AI_KEY };
  if (env.GROQ_API_KEY) return { provider: 'llm', model: 'groq/openai/gpt-oss-120b', apiKey: env.GROQ_API_KEY };
  return undefined;
}

/** Ready-to-use voice decider (voiceDeciderOptions), or undefined. The Groq LLM backend runs gpt-oss at reasoning_effort
 *  'low' (measured on the leak lists: p50 ~600ms → ~285ms, ~230 → ~80 output tokens, accuracy 33/38 → 33-36/38) and every
 *  call is cut at `timeoutMs` (aborted — a late verdict is unwanted, and a client retry must not keep running behind it). */
export function voiceDecider(env: Record<string, string | undefined> = process.env, timeoutMs = 1500): Decider | undefined {
  const o = voiceDeciderOptions(env);
  if (!o) return undefined;
  const ai = new AIClient();
  const llm = new LlmDecideAdapter({ client: { chat: (c: ChatOptions) => ai.chat({ reasoningEffort: 'low', ...c }) } as AIClient });
  return new DecideClient({ ...o, timeoutMs, adapters: { typesafe: new JevAdapter(), llm } }) as unknown as Decider;
}

export interface InstinctContext {
  /** What the agent said last (or is saying now). */
  agentSaid?: string;
  /** Short recent-conversation tail, oldest first. */
  recent?: string;
}

export interface InstinctDecision {
  kind: 'listen' | 'overlap' | 'barge_intent' | 'ack' | 'channel';
  heard: string;
  choice: string;
  probabilities: Record<string, number>;
  ms: number;
  acted?: string;
}

export class InstinctOptions {
  decider!: Decider;
  /** Partial unchanged this long (a micro-pause) → consult. */
  stableMs = 250;
  /** Don't consult on fragments shorter than this. */
  minWords = 4;
  /** Act only on the TOP choice, and only above its floor (live eval 2026-09-23: a correct cut-in often
   *  tops at 0.6-0.8; a mid-thought "we could maybe" drew a 0.58 backchannel that must not fire). */
  backchannelMin = 0.65;
  interjectMin = 0.6;
  yieldMin = 0.7;
  backchannelGapMs = 5000;
  interjectGapMs = 20_000;
  /** A decision whose partial grew by this many words while in flight is stale → dropped. */
  staleWords = 3;
  /** Overlap: consult only once the engine has HELD the pause this long without ceding. The engine cedes on
   *  the 2nd novel word (~200ms after the pause, live 2026-09-23) — a call fired at the pause lands after that
   *  (~300-400ms) and can never act. A hold that survives this window is the undecided case (a 1-word overlap
   *  the engine would RESUME over at overlapResumeMs=700) — the one where a yield actually changes the outcome. */
  overlapSettleMs = 250;
  /** ackFor waits this long for the host's ackOutcome before treating the ack as unspoken. Hosts set it to the engine's
   *  last chance to fire (bod-voice: 2×adaptiveAckMs — past that a pending pick is dropped as pick_late). */
  ackSettleMs = 1500;
  context: () => InstinctContext = () => ({});
  /** Actions return `false` when the host could not act (engine state moved on) → logged as `<act>_skipped`. */
  onBackchannel: () => boolean | void = () => {};
  onInterject: (heard: string) => boolean | void = () => {};
  onYield: (heard: string) => boolean | void = () => {};
  /** The overlap was judged a listener cue — carry on. Hosts whose engine HOLDS short overlaps for a verdict
   *  (bod-voice overlapDecideMs) resume it here; otherwise leave the default no-op (the engine decides). */
  onKeepTalking: (heard: string) => boolean | void = () => {};
  onDecision: (d: InstinctDecision) => void = () => {};
  now: () => number = () => performance.now();
}

export type BargeIntent = 'resume' | 'aside' | 'steer' | 'stop';

/** What the user's finished turn calls for, as the first sound of our reply (pickAck's Choice). */
export const ACK_SITUATIONS = {
  hard_question: 'a question that takes real thought, judgment or looking something up ("what\'s the biggest takeaway?", "how should I structure this?")',
  simple_question: 'a quick question with an instant, obvious answer ("how are you?", "what time is it?", "can you hear me?")',
  emotional_bad: 'they share something bad, sad, painful, worrying or frustrating',
  emotional_good: 'they share good news, excitement, relief or pride',
  challenge: 'they doubt, question or push back on what you just said ("are you sure?", "why do you say that?", "that doesn\'t sound right")',
  surprise: 'they react with surprise or disbelief to what you said ("oh, that\'s new", "really?", "no way", "oh.")',
  agreement: 'they agree with you, confirm, or tell you to go on ("exactly", "yes, that\'s it", "okay, so continue")',
  goodbye: 'they are wrapping up the conversation or saying goodbye',
  command: 'a request or instruction for you to do, find, check or tell them something ("tell me the news", "check my calendar")',
  silent: 'ONLY when nothing was really said yet: an unfinished fragment or false start, a mic check or count ("one, two"), a bare greeting or filler ("hey", "um")',
} as const;
export type AckSituation = keyof typeof ACK_SITUATIONS;

/** THE ack bank — one data object (a forecast step may inject contextual phrases per key). Situations are picked by
 *  pickAck's Choice; the barge keys by the interruption's intent. Phrases are what gets spoken + joins the echo set:
 *  none is a lone word or a stock reply a user says on its own ("okay", "exactly", "sounds good", "oh no") — the
 *  engine drops a user utterance equal to the last ack as its echo for 6s (live: the user's "Exactly." got "Exactly."
 *  back, so their next "Exactly." would have been swallowed). Enforced by a test. `silent` is empty on purpose. */
export const ACK_BANK: Record<AckSituation | BargeIntent, string[]> = {
  hard_question: ['Hmm, let me think on that.', 'Ooh, interesting one.', "Hm, that's a big one.", "Let's see here."],
  simple_question: ['Oh, well, so—', "Well, let's see—", 'Oh, easy one—'],
  challenge: ['Fair question.', 'Mm, fair to ask.', 'Hm, okay, so—', 'Ah, I get why—'],
  emotional_bad: ["Oh, I'm sorry.", "Ugh, that's rough.", "Aw, that's hard.", "Oh man, I'm sorry."],
  emotional_good: ["Oh, that's great!", "Oh, that's awesome!", 'Oh, nice one!', 'Love to hear it!'],
  surprise: ['Right? Pretty wild.', 'Yeah, wild, huh?', 'Huh, I know.', "Wild, isn't it?"],
  agreement: ['Mm, good, so—', 'Yeah, so—', 'Alright, so—', 'Yep, with you.'],
  goodbye: ['Alright, catch you later.', 'Great chatting.', 'Okay, all set then.'],
  command: ['On it.', 'Sure, one sec.', 'Got it, one moment.', 'Sure thing, one sec.'],
  silent: [],
  // interruption acks, keyed on the barge intent
  stop: ['Sure, stopping.', 'Okay, stopping here.', "Got it, I'll stop."],
  aside: ['Oh, quick one—', 'Ah, sure, so—', 'Oh, right, so—'],
  steer: ['Ah, okay, switching—', 'Ah, got it, so—', 'Oh, gotcha, sure—'],
  resume: ['Right, so—', 'Anyway, so—', 'So, as I was saying—'],
};

/** Duplex v2 second-channel actions (Instinct.channel's Choice). The silence bias lives in the CRITERIA, not prose: a
 *  greeting or a quick full turn the reflex answers at once scores `silence` (v1's failure: "hmm ok" after a bare "hey"). */
export const CHANNEL_ACTIONS = {
  silence: 'the default — nothing to add. ALWAYS for a greeting, thanks, a yes/no, or any short complete turn the real reply will answer instantly ("hey", "how are you?", "thanks"); also mid-word or mid-clause',
  backchannel: 'they are mid-way through a longer story or explanation (several clauses) and just closed a clause: a tiny "mm-hm"/"right" shows you follow, without taking the turn',
  filler: 'they paused mid-thought, searching for words, and a soft "hmm" keeps the line warm',
  bridge: 'they finished a substantial request or hard question and the real answer takes a moment: a short "let me check—" lead-in',
} as const;
export type ChannelKind = keyof typeof CHANNEL_ACTIONS;

/** Canned 0ms lines per second-channel action (rotated without the last picks; see SecondChannel). */
export const CHANNEL_BANK: Record<Exclude<ChannelKind, 'silence'>, string[]> = {
  backchannel: ['Mm-hm.', 'Right.', 'Mm, yeah.', 'Uh-huh.', 'I see.', 'Mm.'],
  filler: ['Hmm…', 'Mm…', 'Hm, mm…', 'Ahh…'],
  bridge: ['Let me check—', 'One sec, looking—', 'Hm, let me see—', 'Okay, give me a moment—', 'Let me think on that—'],
};

/** After a barge, the situations the interruption's OWN ack replaces; any other situation keeps its situational ack.
 *  A steer that asks something still gets a question ack (live: "Hello? How are you today?" → "Oh, alright—"); a
 *  resume's "okay, so continue" is a go-on, not agreement with a claim (live: → "Exactly."). A stop, aside or steer
 *  the Choice calls silent (an unfinished fragment) stays silent — the user is still mid-thought (live 20260923-192543:
 *  "But isn't it" → "Oh, right, so—", then the rest of their sentence talked over it). */
const INTENT_ACK_OVER: Record<BargeIntent, AckSituation[]> = {
  stop: (Object.keys(ACK_SITUATIONS) as AckSituation[]).filter((s) => s !== 'silent'),
  steer: ['command'],
  resume: ['agreement', 'silent'],
  aside: [],
};

/** The engine's AckContext (bod-voice `pickAck`) — structural, so this module needs no engine import. */
export interface AckAsk { text: string; question?: boolean; barged?: boolean }

/** Model-facing note for a turn that follows a mid-speech interruption: carries the agent's ORIGINAL intent
 *  (the full cut reply + the part the user never heard) and how to proceed. `intent` unknown → the model decides.
 *  The heard/unheard split is an ESTIMATE (played ms × ~15 chars/s) that lands mid-word ("…larger contex"), and
 *  the reflex parroted whatever fragment it was shown (live 20260923-102258: "I heard you only caught "…larger
 *  contex"", "You caught only "I h""). So: the split snaps to a word boundary, the heard part is never quoted
 *  (only the missed remainder is), near-nothing heard is stated plainly, and narrating it is forbidden.
 *  Also the note for an ASIDE over a cut worker delivery (DuplexAgent.asideNote): the reflex answers and carries the
 *  delivery on itself — so it is told outright that everything before the missed part was heard, and to say each
 *  point once (live 20260923-192543: the answer covered the use cases, then the replay said them again). */
export function interruptionNote(cut: { full: string; heard: string }, intent?: BargeIntent): string {
  const full = cut.full.trim();
  let at = Math.min(cut.heard.length, cut.full.length);
  if (/\S/.test(cut.full[at] ?? '') && /\S/.test(cut.full[at - 1] ?? '')) at = cut.full.lastIndexOf(' ', at) + 1; // mid-word → back to its start
  const heardWords = cut.full.slice(0, at).trim().split(/\s+/).filter(Boolean).length;
  const unheard = cut.full.slice(at).trim();
  const landed = heardWords < 4
    ? 'They cut in almost at once — treat all of it as unsaid.'
    : `They heard everything before this part. They missed the rest of it: "${unheard}".`;
  const how: Record<BargeIntent | 'unknown', string> = {
    resume: 'They did not ask anything new. Carry on with the part they missed — no restart, no apology.',
    aside: 'It was an aside. Answer it briefly FIRST, then finish the part they missed, adjusted to anything they just said. Say each point once: skip whatever they already heard and whatever your answer just covered.',
    steer: 'They steered away. Follow their new direction; carry over only what still matters from your unfinished point, or drop it.',
    stop: 'They want you to stop. Do not resume; at most a few words of acknowledgement.',
    unknown: 'Decide like a person would: if it was an aside, answer it and then finish your point; if they steered away, follow them and drop or adapt the rest.',
  };
  return `\n[the user interrupted you mid-speech. You were saying: "${full}" ${landed} ${how[intent ?? 'unknown']} ` +
    'Just talk, like a person picking the thread back up: never tell them what they heard or missed, never say where you were cut off, and never quote your earlier words back. ' +
    'If their words are still an unfinished fragment, call Hold instead of answering.]';
}

const FRAME = 'You are the instinct of a warm, attentive human in a live spoken conversation. ' +
  'Decide what a natural human would do THIS instant — the way people do without thinking. ';

const acted = (what: string, r: boolean | void) => (r === false ? `${what}_skipped` : what);
const words = (s: string) => (s.trim() ? s.trim().split(/\s+/).length : 0);

export class Instinct {
  private partial = '';
  private turnStart = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inflight = false;
  private lastBcAt = -Infinity;
  private lastInterjectAt = -Infinity;
  private lastIntent?: { heard: string; p: Promise<BargeIntent> };
  private overlapGen = 0; // bumped by overlapEnded / a newer overlap → cancels a pending or in-flight overlap consult
  private lastAckPick?: { text: string; p: Promise<string | null>; spoken: Promise<string | null>; settle: (said: string | null) => void };
  private interjecting = false; // inside onInterject — see pickAck
  private lastAckSaid = '';

  public constructor(public options: Partial<InstinctOptions> & { decider: Decider }) {
    this.options = { ...new InstinctOptions(), ...options };
  }
  private get o(): InstinctOptions { return this.options as InstinctOptions; }

  /** A live user partial while the agent is LISTENING. Consults once the partial goes stable. */
  hear(text: string): void {
    const t = text.trim();
    if (!t || t === this.partial) return;
    this.partial = t;
    if (!this.turnStart) this.turnStart = this.o.now();
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = null; void this.consult(t); }, this.o.stableMs);
  }

  /** The user's turn ended (final dispatched, or the agent took the floor) — forget the partial, and drop any
   *  pending overlap consult: whatever hold it was about is over. */
  reset(): void {
    this.overlapGen++;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.partial = '';
    this.turnStart = 0;
  }

  private async consult(heard: string): Promise<void> {
    if (this.inflight || words(heard) < this.o.minWords) return;
    this.inflight = true;
    try {
      const now = this.o.now();
      const d = await this.ask('listen', heard, {
        heard_so_far: heard,
        user_speaking_for_ms: Math.round(now - this.turnStart),
        ms_since_our_last_backchannel: Number.isFinite(this.lastBcAt) ? Math.round(now - this.lastBcAt) : null,
      }, FRAME + 'The user is mid-turn and just paused for a split second. Do you keep quiet, give a tiny ' +
        'listener cue, or cut in? Cut in only when a human really would: they are clearly wrong about something ' +
        'that matters, they are struggling for a word you know, or they asked something and are trailing off.', {
        stay_silent: 'keep listening — they are mid-thought, nothing to add',
        backchannel: 'a tiny "mm-hm"/"right" to show you are following, without taking the turn (fits after a clause in a longer story or explanation)',
        interject: 'cut in now with a short correction, the word they are reaching for, or an answer',
      });
      if (!d) return;
      // Stale: the user kept talking well past what we judged, or the turn already ended.
      if (!this.partial || words(this.partial) - words(heard) >= this.o.staleWords) return this.o.onDecision({ ...d, acted: 'stale' });
      const p = d.probabilities, t = this.o.now();
      if (d.choice === 'interject' && p.interject >= this.o.interjectMin && t - this.lastInterjectAt > this.o.interjectGapMs) {
        this.lastInterjectAt = t;
        const at = this.partial;
        this.reset();
        this.interjecting = true;
        let r: boolean | void;
        try { r = this.o.onInterject(at); } finally { this.interjecting = false; }
        this.o.onDecision({ ...d, acted: acted('interject', r) });
      } else if (d.choice === 'backchannel' && p.backchannel >= this.o.backchannelMin && t - this.lastBcAt > this.o.backchannelGapMs) {
        this.lastBcAt = t;
        this.o.onDecision({ ...d, acted: acted('backchannel', this.o.onBackchannel()) });
      } else this.o.onDecision(d);
    } finally {
      this.inflight = false;
    }
  }

  /** The user started talking OVER the agent (engine paused on a genuine overlap). Yield or keep going?
   *  Consulted only if the engine is STILL holding after overlapSettleMs — see overlapEnded(). */
  async overlap(heard: string): Promise<void> {
    if (!heard.trim()) return;
    const gen = ++this.overlapGen;
    if (this.o.overlapSettleMs > 0) await new Promise((r) => setTimeout(r, this.o.overlapSettleMs));
    if (gen !== this.overlapGen) return; // the engine already decided (ceded/resumed) — a call could not matter
    const d = await this.ask('overlap', heard, { user_said_over_us: heard },
      FRAME + 'You are mid-sentence and the user just said something over you. Would a human stop and give them ' +
      'the floor, or treat it as a listener cue and carry on?', {
        yield: 'they want the floor — an objection, question, correction, "stop"/"wait", or new information',
        keep_talking: 'just a listener cue or agreement ("yeah", "mm-hm", "right", "okay") — carry on',
      });
    if (d && gen !== this.overlapGen) this.o.onDecision({ ...d, acted: 'stale' }); // engine decided while we were asking
    else if (d && (d.probabilities.yield ?? 0) >= this.o.yieldMin) {
      this.o.onDecision({ ...d, acted: acted('yield', this.o.onYield(heard)) });
    } else if (d?.choice === 'keep_talking') {
      this.o.onDecision({ ...d, acted: acted('keep_talking', this.o.onKeepTalking(heard)) });
    } else if (d) this.o.onDecision(d);
  }

  /** The engine's hold ended (it ceded the floor, resumed over a stalled overlap, began a new turn, or was
   *  interrupted) — drop any pending/in-flight overlap consult. Hosts call this on every hold exit. */
  overlapEnded(): void { this.overlapGen++; }

  /** What the user meant by cutting us off mid-speech. Memoized per text (the duplex judge and the host's
   *  note builder ask about the same line). Rejects on decider failure — callers pick their fallback. */
  bargeIntent(heard: string): Promise<BargeIntent> {
    if (this.lastIntent?.heard === heard) return this.lastIntent.p;
    const p = this.ask('barge_intent', heard, { user_said_after_cutting_us_off: heard },
      FRAME + 'You were in the middle of saying something (what_we_said_last) and the user cut you off with this. ' +
      'What would a human take from it?', {
        resume: 'nothing new — a filler, apology, or "go on": just pick up where you left off',
        aside: 'a quick question, comment, or clarification about what you were saying — answer it, then carry on with your point',
        steer: 'they are redirecting — a different topic, a correction of your plan, or a new request that replaces what you were saying',
        stop: 'they want you to stop talking or drop it',
      }, true).then((d) => { if (d) this.o.onDecision({ ...d, acted: d.choice }); return (d?.choice as BargeIntent) ?? 'steer'; });
    this.lastIntent = { heard, p };
    return p;
  }

  /** The ack for a finished user turn (engine `pickAck`, asked at dispatch): one Choice over ACK_SITUATIONS, run in
   *  parallel with the reflex. After a barge the interruption's intent (bargeIntent, memoized — the same call the note
   *  builder makes) replaces the situations in INTENT_ACK_OVER with its own ack; the rest keep the situational ack.
   *  `null` = say nothing. Never rejects: a failed Choice → silence (logged in ask) — a wrong ack is worse than none,
   *  and a slow reflex still gets the dead-air repair.
   *  A cut-in (asked from inside onInterject) is always silent: it has no dispatch, so the engine's context carries the
   *  user's PREVIOUS final (live: "My dog died yesterday." re-acked "Oh no." before a cut-in correction), and the
   *  unfinished partial is no turn to react to — the interjection itself is the reply. */
  pickAck(ctx: AckAsk): Promise<string | null> {
    if (this.interjecting) { log.debug(`ack: cut-in turn → silent (engine context "${ctx.text.slice(0, 40)}" is the previous final)`); return Promise.resolve(null); }
    const text = ctx.text.trim();
    const p = (async () => {
      const intentP = ctx.barged ? this.bargeIntent(text).catch((e) => { log.warn(`barge intent for the ack failed — situational ack: ${(e as Error).message}`); return null; }) : null;
      const d = await this.ask('ack', text, { user_just_said: text },
        FRAME + 'The user just finished speaking (user_just_said) and your real reply is a moment away. Classify what they ' +
        'said, so the fitting quick acknowledgement can be voiced first. Pick the category that matches their words; ' +
        'most requests and questions are NOT silent.', ACK_SITUATIONS);
      const sit = (d?.choice ?? 'silent') as AckSituation;
      const intent = intentP ? await intentP : null;
      const key: AckSituation | BargeIntent = intent && INTENT_ACK_OVER[intent].includes(sit) ? intent : sit;
      const phrase = this.rotate(ACK_BANK[key] ?? []);
      if (d) this.o.onDecision({ ...d, acted: phrase ? `${key}: ${phrase}` : `${key}: silent` });
      return phrase;
    })();
    let settle!: (said: string | null) => void;
    const spoken = new Promise<string | null>((r) => { settle = r; });
    // The engine's deadline runs from the pick's start, so does ours: no outcome by then (a slow pick past pick_late,
    // an engine stopped before firing — no diag at all) → not spoken. First settle wins; a later ack_fired is ignored.
    const timer = setTimeout(() => { log.debug(`ack: no engine outcome in ${this.o.ackSettleMs}ms — not claimed`); settle(null); }, this.o.ackSettleMs);
    void spoken.then(() => clearTimeout(timer));
    this.lastAckPick = { text, p, spoken, settle };
    return p;
  }

  /** The host reports what the engine did with the armed ack: the phrase it actually spoke, or null (silent, late,
   *  cancelled). First report per pick wins — a later turn's cancel of an already-spoken ack changes nothing. */
  ackOutcome(said: string | null): void { this.lastAckPick?.settle(said); }

  /** What was said out loud to open the reply to this exact text — lets a host tell the reply turn, without a second
   *  call. Only an ack the engine actually SPOKE (ackOutcome): a pick that lands after the engine's deadline is never
   *  claimed. null: nothing picked, not spoken, not asked, or no outcome within ackSettleMs of the pick's start.
   *  Never waits past that cap (a hung pick included), and returns as soon as the outcome settles. */
  async ackFor(text: string): Promise<string | null> {
    const pick = this.lastAckPick;
    if (pick?.text !== text.trim()) return null;
    return Promise.race([pick.p.then((p) => (p ? pick.spoken : null)), pick.spoken]);
  }

  /** Duplex v2 second channel: which (if any) listener line fits THIS instant — one Choice over CHANNEL_ACTIONS.
   *  `heard` is the micro-context tail only (never history). Never rejects: a failed call → null (the rule decides). */
  channel(heard: string, state: Record<string, unknown>): Promise<InstinctDecision | null> {
    return this.ask('channel', heard, { heard_so_far: heard, ...state },
      FRAME + 'You are the quick side-voice of the conversation, NOT the one who answers. Pick the tiny sound a person ' +
      'would make right now, or none. Silence is the default: most moments call for nothing.', CHANNEL_ACTIONS);
  }

  /** A random phrase from `pool`, never the one said last (no back-to-back repeat across keys). */
  private rotate(pool: string[]): string | null {
    const fresh = pool.filter((x) => x !== this.lastAckSaid);
    const from = fresh.length ? fresh : pool;
    if (!from.length) return null;
    return (this.lastAckSaid = from[Math.floor(Math.random() * from.length)]);
  }

  private async ask(kind: InstinctDecision['kind'], heard: string, state: Record<string, unknown>,
    instructions: string, criteria: Record<string, string>, rethrow = false): Promise<InstinctDecision | null> {
    const t0 = this.o.now();
    try {
      const ctx = this.o.context();
      const { answers } = await this.o.decider.decide({
        state: { ...state, what_we_said_last: ctx.agentSaid || null, recent_conversation: ctx.recent || null },
        questions: { act: { type: 'choice', instructions, criteria } },
      });
      return { kind, heard, choice: answers.act.choice, probabilities: answers.act.probabilities, ms: Math.round(this.o.now() - t0) };
    } catch (e) {
      log.warn(`${kind} decision failed — falling back to engine defaults: ${(e as Error).message}`);
      if (rethrow) throw e;
      return null;
    }
  }
}
